import { hasKey, t } from '@app/i18n';
import type { components } from '@app/api/types';

/**
 * Rótulo do ambiente do tenant — **fonte única**.
 *
 * O valor que vem do contrato é uma chave de enum sem acento (`homologacao`), e
 * ela estava chegando crua à topbar: o cliente lia `Vivo · homologacao` em todas
 * as quinze telas, ao lado do próprio nome. O elenco (§4.9) fixa os rótulos
 * acentuados, e é isto que a tela mostra.
 *
 * Mora em `app/` e não na topbar porque o mesmo rótulo reaparece assim que a
 * identidade ganhar `ambiente` — cada identidade do elenco tem o seu, e a
 * distinção `prod` × `hmg` é o que a regra de aprovação do P14 usa.
 */

export type TenantLabel = components['schemas']['TenantConfig']['label'];

/** Os dois valores do enum, na ordem em que a tela os lista. */
export const TENANT_LABELS: readonly TenantLabel[] = ['producao', 'homologacao'];

/**
 * Rótulo do TENANT (eixo B) para a tela, tolerante ao que ainda não conhecemos:
 * valor fora do enum volta como veio, porque esconder um rótulo que o servidor
 * mandou é pior que mostrá-lo sem acento.
 *
 * # SÓ O EIXO B, E ISSO É O CONSERTO DO LOTE 3 · F0-a
 *
 * Esta função se chamava `ambienteLabel` e era chamada por DUAS telas com
 * significados diferentes: a topbar/T21 com `producao|homologacao` (o rótulo do
 * cliente) e o formulário de identidade com `dev|hmg|prod` (onde o workload roda).
 *
 * Funcionava por acidente: só o eixo B tem chave no catálogo, então o eixo A caía
 * no `return ambiente` e mostrava o slug cru. Ou seja, a função "servia" aos dois
 * porque **não fazia nada** para um deles — o pior tipo de reuso, o que só aparece
 * quando alguém acrescenta a chave que falta e a outra tela muda sozinha.
 *
 * Agora é do eixo B, e o nome diz. Quem mostra o ambiente de uma identidade usa o
 * valor cru até o cadastro existir (Lote 3 · F1-c, quando o rótulo passa a ser o
 * `name` do ambiente cadastrado).
 */
export function tenantLabelText(label: string | null | undefined): string {
  if (!label) return '';
  const chave = `common.tenantLabel.${label}`;
  return hasKey(chave) ? t(chave) : label;
}

/*
 * A PRECEDÊNCIA DA D-6 SAIU DAQUI (Lote 3 · F0-b)
 *
 * Este arquivo tinha uma cópia da tabela de sinônimos e da precedência do
 * servidor — `AMBIENTE_SYNONYMS`, `normalizeAmbiente`, `identityAmbiente` e
 * `ambienteDoNamespace` —, mantida em espelho por `__tests__/ambiente.test.ts`
 * contra `control-plane/backend/domain/ambiente_test.go`.
 *
 * A cópia saiu por dois motivos, e o segundo é o que decide:
 *
 *  1. ela alcançava só DOIS dos quatro elos. A label da imagem e a do cluster
 *     vêm antes do declarado e do sufixo, e o navegador não tem como lê-las —
 *     por isso a prévia não podia nomear a origem sem arriscar mentir, e a tela
 *     caía num texto genérico;
 *  2. eram duas implementações da mesma ordem, e um espelho só falha DEPOIS de
 *     alguém mudar um lado. O dia em que um elo mudasse, a prévia e a gravação
 *     discordariam — e quem visse a tela não teria como saber qual das duas
 *     estava certa.
 *
 * Agora a prévia é uma leitura:
 * `GET /api/v1/identities/{id}/environment-preview`, respondida pela mesma
 * função que grava. O que sobrou aqui é o que é MESMO da tela — rótulo e texto
 * de origem —, e nada disso é regra de negócio.
 */

/**
 * Qual elo da precedência respondeu pelo ambiente efetivo — o `ambiente_origem`
 * de `Identity` (30/08). Vocabulário do servidor
 * (`control-plane/backend/domain.OrigemDoAmbiente`); `null` é "não confirmado".
 */
export type AmbienteOrigin = NonNullable<components['schemas']['Identity']['environment_origin']>;

/**
 * A frase que T5 mostra sob o campo **Ambiente efetivo**.
 *
 * Ela existe por causa de um defeito de tela, não de dado: o ensaio de 30/08
 * mostrou a cena travar entre «Ambiente» e «Ambiente declarado» — dois rótulos
 * quase iguais, um acima do outro, e só o segundo abre lista. Dizer de onde veio
 * o efetivo é o que responde, em dois segundos, *por que* ele não se mexe quando
 * a pessoa mexe no outro.
 *
 * Por isso cada frase termina apontando para onde se muda, menos a do próprio
 * declarado (que é onde já se está) e a de `nenhum` (que é um convite a
 * declarar).
 */
/**
 * Texto de apoio do campo somente leitura. `null`/desconhecido devolve `null`, e
 * a tela cai no texto genérico: o servidor só nomeia o elo quando tem certeza, e
 * inventar uma origem plausível seria pior que não dizer nenhuma.
 */
export function ambienteOriginText(origem: string | null | undefined): string | null {
  if (!origem) return null;
  // `hasKey` no lugar da tabela: o `t()` devolveria a própria chave para um elo
  // que o servidor invente, e a tela pintaria `common.environmentOrigin.xyz`.
  const chave = `common.environmentOrigin.${origem}`;
  return hasKey(chave) ? t(chave) : null;
}
